import React from 'react'
import { Tooltip } from '@mui/material'
import {
  Add as AddIcon, ArrowBack, ArrowForward, DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon,
  PictureAsPdfOutlined, VisibilityOutlined, ContentCopy, Block as DeactivateIcon,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { api } from '../function/axios'
import { formatDate } from '../function/date'
import { useDispatch } from '../store'
import { downloadBlob } from '../store/support/service'
import { deactivateQrRegistration } from '../store/qrRegistration/actionCreator'
import { QrRegistrationResult } from '../store/qrRegistration/actionType'
import { useAdminTheme } from '../hook/useAdminTheme'
import { QrCreateDialogV2, QrPreviewDialogV2, qrPdfName } from '../component/qr-registration-v2/QrDialogsV2'
import { fetchQrPdf, QrStatus, qrStatus, qrWhen, STATUS_COLOR, STATUS_LABEL } from '../component/qr-registration-v2/qrRegistration'
import l from './SubscribersPageV2.module.css'
import s from '../component/qr-registration-v2/QrRegistrationV2.module.css'

/*
 * QR registration in the v2 admin design. The classic page
 * (QrRegistrationPage.tsx) is untouched; this one reads the same list
 * (GET /auth/qr-registration — the whole list, not paged), creates and
 * deactivates through the same saga actions, and downloads the same PDF.
 *
 * New on top: totals by status, status filters and search, the time to the
 * next change ("ends in 3 days"), the printed poster previewed in the page
 * (the backend's own PDF) and a create dialog with a live poster mock.
 * How many people signed up through a link is not here — the backend keeps
 * no count per link (sign-ups carry it only as their utm campaign).
 */

const PER_PAGE = 10
type Filter = 'all' | QrStatus

const FILTERS: [Filter, string][] = [['all', 'All'], ['active', 'Active'], ['scheduled', 'Scheduled'], ['expired', 'Expired'], ['inactive', 'Deactivated']]

const shortDate = (v?: string | null) => (v ? formatDate(v, 'dd.MM.yyyy HH:mm') : null)

export function QrRegistrationPageV2() {
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()

  const [rows, setRows] = React.useState<QrRegistrationResult[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [filter, setFilter] = React.useState<Filter>('all')
  const [page, setPage] = React.useState(0)
  const [creating, setCreating] = React.useState(false)
  const [preview, setPreview] = React.useState<QrRegistrationResult | null>(null)
  const [toDeactivate, setToDeactivate] = React.useState<QrRegistrationResult | null>(null)
  const [busyPdf, setBusyPdf] = React.useState<number | null>(null)
  const [copied, setCopied] = React.useState<number | null>(null)

  const load = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/auth/qr-registration' })
      const list: QrRegistrationResult[] = Array.isArray(data) ? data : data?.list || []
      setRows(list.slice().sort((a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()))
    } catch {
      setRows([])
    }
  }, [])
  React.useEffect(() => { void load() }, [load])
  React.useEffect(() => { setPage(0) }, [query, filter])

  const all = rows || []
  const now = Date.now()
  const count = (st: QrStatus) => all.filter((r) => qrStatus(r, now) === st).length
  const q = query.trim().toLowerCase()
  const list = all.filter((r) =>
    (filter === 'all' || qrStatus(r, now) === filter)
    && (!q || (r.label || '').toLowerCase().includes(q) || r.joinUrl.toLowerCase().includes(q)))
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const shown = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  async function download(row: QrRegistrationResult) {
    setBusyPdf(row.id)
    try {
      downloadBlob(await fetchQrPdf(row.id), qrPdfName(row))
    } catch {
      // the api layer reports it
    } finally {
      setBusyPdf(null)
    }
  }
  function copy(row: QrRegistrationResult) {
    void navigator.clipboard?.writeText(row.joinUrl).catch(() => undefined)
    setCopied(row.id)
    window.setTimeout(() => setCopied((c) => (c === row.id ? null : c)), 1300)
  }
  function deactivate() {
    if (!toDeactivate) return
    const id = toDeactivate.id
    dispatch(deactivateQrRegistration(id, (updated) => {
      setRows((r) => (r ? r.map((x) => (x.id === id ? { ...x, ...updated, isActive: false } : x)) : r))
      setPreview((p) => (p && p.id === id ? { ...p, ...updated, isActive: false } : p))
    }))
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Growth</small><b>QR registration</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by label or link" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>QR links</small><b>{rows ? all.length : '—'}</b><span>printable sign-up codes</span></div>
          <div className={l.kpi}><small>Active</small><b style={{ color: 'var(--ok)' }}>{rows ? count('active') : '—'}</b><span>scanning works now</span></div>
          <div className={l.kpi}><small>Scheduled</small><b>{rows ? count('scheduled') : '—'}</b><span>start later</span></div>
          <div className={l.kpi}><small>Not working</small><b>{rows ? count('expired') + count('inactive') : '—'}</b><span>{rows ? `${count('expired')} expired · ${count('inactive')} deactivated` : ''}</span></div>
        </div>

        <div className={l.sec}>
          <div className={s.chips}>
            {FILTERS.map(([k, label]) => (
              <button key={k} type="button" className={`${l.chip} ${filter === k ? l.chipOn : ''}`} onClick={() => setFilter(k)}>
                {label}{k !== 'all' && rows ? <span className={s.chipCount}>{count(k)}</span> : null}
              </button>
            ))}
          </div>
          <div className={l.spacer} />
          <button type="button" className={s.primary} onClick={() => setCreating(true)}><AddIcon sx={{ fontSize: 17 }} />New QR link</button>
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {rows === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !shown.length ? (
              <div className={l.empty}>{q || filter !== 'all' ? 'Nothing matches the filter' : 'No QR links yet — create the first one'}</div>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Link</th>
                    <th>Status</th>
                    <th>Validity</th>
                    <th>Join link</th>
                    <th>Created</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const st = qrStatus(r, now)
                    const when = qrWhen(r, now)
                    return (
                      <tr key={r.id} onClick={() => setPreview(r)} title="Show the poster">
                        <td>
                          <div className={s.name}>
                            <span className={s.qrIcon} style={{ opacity: st === 'active' ? 1 : 0.5 }} aria-hidden><i /><i /><i /></span>
                            <div>
                              <b>{r.label || <span className={l.muted}>No label</span>}</b>
                              <span className={l.sub}>#{r.id} · {r.hasBackgroundImage ? 'own background' : 'gradient background'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={l.status}><span className={l.dot} style={{ background: STATUS_COLOR[st] }} />{STATUS_LABEL[st]}</span>
                          {when && <span className={l.sub}>{when}</span>}
                        </td>
                        <td className={l.nowrap}>
                          {r.validFrom || r.validUntil ? (
                            <>
                              <span>{shortDate(r.validFrom) || 'any time'}</span>
                              <span className={l.sub}>→ {shortDate(r.validUntil) || 'no end'}</span>
                            </>
                          ) : <span className={l.muted}>No limits</span>}
                        </td>
                        <td><code className={s.url} title={r.joinUrl}>{r.joinUrl}</code></td>
                        <td className={l.nowrap}>{formatDate(r.createdTime, 'dd.MM.yyyy')}<span className={l.sub}>{formatDate(r.createdTime, 'HH:mm')}</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className={s.actions}>
                            <Tooltip title="Show the poster"><button type="button" className={s.act} onClick={() => setPreview(r)} aria-label="Show the poster"><VisibilityOutlined sx={{ fontSize: 17 }} /></button></Tooltip>
                            <Tooltip title={copied === r.id ? 'Copied' : 'Copy the join link'}><button type="button" className={s.act} onClick={() => copy(r)} aria-label="Copy the join link"><ContentCopy sx={{ fontSize: 16 }} /></button></Tooltip>
                            <Tooltip title="Download PDF">
                              <button type="button" className={s.act} onClick={() => { void download(r) }} disabled={busyPdf === r.id} aria-label="Download PDF"><PictureAsPdfOutlined sx={{ fontSize: 17 }} /></button>
                            </Tooltip>
                            <Tooltip title={r.isActive ? 'Deactivate' : 'Already deactivated'}>
                              <span>
                                <button type="button" className={`${s.act} ${s.actDanger}`} onClick={() => setToDeactivate(r)} disabled={!r.isActive} aria-label="Deactivate"><DeactivateIcon sx={{ fontSize: 16 }} /></button>
                              </span>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {pages > 1 && (
            <div className={s.pager}>
              <span>{page * PER_PAGE + 1}–{Math.min(list.length, (page + 1) * PER_PAGE)} of {list.length}</span>
              <div className={s.actions}>
                <button type="button" className={s.act} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
                <button type="button" className={s.act} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {creating && <QrCreateDialogV2 theme={theme} onClose={() => setCreating(false)} onCreated={() => { void load() }} />}
      {preview && <QrPreviewDialogV2 row={preview} theme={theme} onClose={() => setPreview(null)} onDeactivate={setToDeactivate} />}
      {toDeactivate && (
        <ConfirmV2
          header={`Deactivate “${toDeactivate.label || `QR link #${toDeactivate.id}`}”? Printed posters stop working — this cannot be undone here.`}
          onYes={deactivate}
          onClose={() => setToDeactivate(null)}
        />
      )}
    </PrivatePage>
  )
}
